import { inject, Injectable } from '@angular/core';
import { Course, CourseDTO } from '../models/course.model';
import { catchError, delay, map, Observable, of, tap } from 'rxjs';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { DialogService } from '../../shared/services/dialog.service';
import { COURSE_FORM } from '../models/form/course-form';
import { ApiService } from '../../shared/services';
import { PayloadRequest, PayloadResponse } from '../models/payload.model';
import { HttpParams } from '@angular/common/http';
import { FilterFieldMap } from '../models';
import { trimFormValues } from '../../shared/utils';
import { DEFAULT_END_DATE } from '../constants/date.constants';

@Injectable({
	providedIn: 'root'
})
export class CourseService {
	private apiService = inject(ApiService);
	private dialogService = inject(DialogService);
	constructor(
		private fb: FormBuilder,
		private router: Router
	) {}

	createCourseForm(): FormGroup {
		const formBuilder = new FormBuilder();
		const formControls: { [key: string]: FormControl } = {};

		Object.keys(COURSE_FORM).forEach(key => {
			const field = COURSE_FORM[key];
			formControls[key] = new FormControl(field.value, field.validators);
		});

		return formBuilder.group(formControls);
	}

	loadCourseData(id: number, form: FormGroup): Observable<CourseDTO> {
		return this.getCourseById(id).pipe(
			tap(course => {
				form.patchValue(course);
			})
		);
	}

	navigateToList(): void {
		this.router.navigate(['/main/corsi']);
	}

	getCourseFormFields(): Observable<any> {
		return of(COURSE_FORM);
	}

	getCoursesList(): Observable<PayloadResponse<Course[]>>;
	getCoursesList(
		filter: FilterFieldMap,
		pageNumber?: number,
		pageSize?: number
	): Observable<PayloadResponse<Course[]>>;
	getCoursesList(
		filter?: FilterFieldMap,
		pageNumber?: number,
		pageSize?: number
	): Observable<PayloadResponse<Course[]>> {
		let params = new HttpParams();
		if (filter) {
			if (filter['title'].value)
				params = params.set('title', filter['title'].value);
			if (filter['nameCourseMoodle'].value)
				params = params.set(
					'nameCourseMoodle',
					filter['nameCourseMoodle'].value
				);
			if (filter['isActive'].value)
				params = params.set('isActive', filter['isActive'].value);
		}

		if (pageNumber) params = params.set('pageNumber', pageNumber);
		if (pageSize) params = params.set('pageSize', pageSize);
		return this.apiService.get('courses', { params: params });
	}

	countItems(): Observable<{ activeItems: number; inactiveItems: number }> {
		return this.apiService
			.get('courses/count-status')
			.pipe(map(payload => payload.data));
	}

	getCourseById(courseId: number): Observable<CourseDTO> {
		return this.apiService.get(`courses/${courseId}`).pipe(
			map(course => {
				return {
					...course.data,
					idCourseMoodle: course.data.idCourseMoodle,
					updateIdCourseMoodle: course.data.updateIdCourseMoodle,
					availableTemplatesId:
						course.data.availableTemplates?.courseTemplateId ?? null,
					riskId: course.data.risk?.id ?? null,
					toComplete: course.data.toComplete
				};
			})
		);
	}

	onSubmitForm(
		courseId: number,
		formCourse: FormGroup
	): Observable<PayloadResponse<Course>> {
		trimFormValues(formCourse);

		if (formCourse.invalid) {
			formCourse.markAllAsTouched();

			this.dialogService.openDialogInfoNoRedirect(
				'Errore',
				'Compila tutti i campi obbligatori.'
			);

			return of();
		}

		const isUpdate = !!courseId;
		const course = this.convertTypeForm(formCourse);

		const observable = isUpdate
			? this.updateCourse(course)
			: this.saveCourse(course);

		return observable.pipe(
			tap(res =>
				this.showDialogSuccess(isUpdate, res?.metadata?.['titleSyncWarning'])
			)
		);
	}
	private saveCourse(course: CourseDTO): Observable<PayloadResponse<Course>> {
		const payload = new PayloadRequest<CourseDTO>({ data: course });
		return this.apiService.post(`courses/create`, payload);
	}

	private updateCourse(course: CourseDTO): Observable<PayloadResponse<Course>> {
		const payload = new PayloadRequest<CourseDTO>({ data: course });
		return this.apiService.patch(`courses/update`, payload);
	}

	private showDialogSuccess(
		isUpdate: boolean,
		titleSyncWarning?: string
	): void {
		let dialogMessage = isUpdate
			? 'Il corso è stato aggiornato correttamente'
			: 'Il corso è stato creato con successo';

		// Warning non bloccante: il salvataggio è comunque andato a buon fine, ma il titolo non è
		// stato propagato su Moodle (servizio disabilitato in questo ambiente o non raggiungibile).
		if (titleSyncWarning) {
			dialogMessage += `. Attenzione: ${titleSyncWarning}`;
		}

		this.dialogService.openDialogAlert(
			'Operazione completata',
			dialogMessage,
			() => {
				this.router.navigate(['/main/corsi']);
			}
		);
	}

	toggleStatusCourse(course: Course): Observable<any> {
		const courseId = course.courseId;
		return this.apiService.patch(`courses/${courseId}/toggle-status`, null);
	}

	/**
	 * Il form non permette più di scegliere/cambiare il corso Moodle collegato: idCourseMoodle,
	 * nameCourseMoodle, updateIdCourseMoodle e updateNameCourseMoodle sono campi hidden,
	 * precaricati dal corso già esistente (loadCourseData) e rispediti invariati al backend.
	 * Il "titolo" mostrato in form (etichettato "Corso Moodle") resta l'unico campo editabile
	 * legato al nome: la sua propagazione a Moodle è gestita interamente lato backend.
	 */
	convertTypeForm(form: FormGroup): CourseDTO {
		const raw = form.getRawValue();

		const courseDto: CourseDTO = {
			...raw,
			courseId: Number(raw.courseId),
			availableTemplatesId: Number(raw.availableTemplatesId),
			riskId: Number(raw.riskId),
			title: raw.title?.trim() || '',
			certificateTitle: raw.certificateTitle?.trim() || '',
			legalReference: raw.legalReference?.trim() || null,
			// isActive non è più un campo di questo form: lo decide il backend (v. CourseService.updateCourse).
			examRequired:
				form.value.examRequired === true || form.value.examRequired === 'true',
			duration: Number(raw.duration),
			percentage: Number(raw.percentage),
			expirationDate: raw.expirationDate || DEFAULT_END_DATE,
			validityYears: raw.validityYears,
			idCourseMoodle: Number(raw.idCourseMoodle),
			nameCourseMoodle: raw.nameCourseMoodle,
			updateIdCourseMoodle: raw.updateIdCourseMoodle ?? null,
			updateNameCourseMoodle: raw.updateNameCourseMoodle ?? null
		};

		return courseDto;
	}

	/**
	 * Converte un Course completo (non "Da Completare") in CourseDTO per l'invio al backend.
	 * Presuppone che il corso sia già completo: va chiamata solo su corsi con toComplete === false.
	 */
	convertCourseToCourseDTO(course: Course): CourseDTO {
		let courseDTO: CourseDTO = {
			courseId: course.courseId,
			idCourseMoodle: course.idCourseMoodle,
			nameCourseMoodle: course.nameCourseMoodle.trim(),
			updateIdCourseMoodle: course.updateIdCourseMoodle,
			updateNameCourseMoodle: course.updateNameCourseMoodle?.trim() ?? '',
			availableTemplatesId: course.availableTemplates?.courseTemplateId ?? null,
			riskId: course.risk?.id ?? null,
			title: course.title,
			certificateTitle: course.certificateTitle,
			legalReference: course.legalReference,
			isActive: course.isActive,
			examRequired: course.examRequired,
			duration: course.duration!,
			percentage: course.percentage!,
			expirationDate: course.expirationDate!,
			validityYears: course.validityYears!
		};
		return courseDTO;
	}
}
